import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    Box,
    Button,
    Chip,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
    useTheme
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useRoleLabels } from 'src/hooks/useRoleLabels';
import { AthletePaymentAssignment, PurposeNames } from '../object-actions/types/types';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';
import { getOfferedPlan } from '../subscriptions/purchaseSteps';
import { assignmentHasPurposeEntitlement } from '../subscriptions/purposeEntitlements';
import { getCoachContentForPurpose, getContentStage } from '../utils/assignmentPermissions';
import ContentScreenshotThumbnail from './ContentScreenshotThumbnail';
import PreAssessmentCta from './PreAssessmentCta';
import SubscriptionPurchaseCta from './SubscriptionPurchaseCta';

const ProgramProgressStepperCoach: React.FC<{ assignment: AthletePaymentAssignment }> = ({ assignment }) => {
    const theme = useTheme();
    const location = useLocation();
    const { applyRoleLabels } = useRoleLabels();

    const assignmentId = assignment.paymentassignment_id ?? assignment.id;
    const entitlements = useMemo(
        () => normalizeEntitlementsMap(assignment.entitlements),
        [assignment.entitlements],
    );
    const offeredPlan = getOfferedPlan(entitlements);

    // State to track which purposes have their previous versions expanded
    const [expandedPurposes, setExpandedPurposes] = useState<Set<string>>(new Set());

    // Helper function to toggle expanded state for a purpose
    const toggleExpanded = (purpose: string) => {
        setExpandedPurposes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(purpose)) {
                newSet.delete(purpose);
            } else {
                newSet.add(purpose);
            }
            return newSet;
        });
    };

    // Get content for each purpose using the enhanced logic
    const docs: Record<keyof typeof PurposeNames, any | null> = {} as any;
    for (const purpose of Object.keys(PurposeNames)) {
        docs[purpose as keyof typeof PurposeNames] = getCoachContentForPurpose(assignment, purpose as keyof typeof PurposeNames);
    }


    // Helper function to format date and time
    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Helper function to get the appropriate screenshot based on theme
    const getScreenshotUrl = (item: any) => {
        if (!item.entity) return null;

        // Use dark theme screenshot if available and theme is dark, otherwise use light
        const isDarkTheme = theme.palette.mode === 'dark';
        if (isDarkTheme && item.entity.screenshot_dark) {
            return item.entity.screenshot_dark;
        } else if (item.entity.screenshot_light) {
            return item.entity.screenshot_light;
        } else if (item.entity.screenshot_dark) {
            return item.entity.screenshot_dark;
        }

        return null;
    };

    // Helper function to get delivery status for each party with checkmarks
    const getDeliveryStatus = (item: any) => {
        const now = new Date();
        const status = {
            athlete: { text: 'Not yet received', hasReceived: false },
            parent: { text: 'Not yet received', hasReceived: false },
            coach: { text: 'Not delivered', hasDelivered: false }
        };

        if (item.entity?.coach_delivered && new Date(item.entity.coach_delivered) < now) {
            status.coach = {
                text: `Delivered ${formatDateTime(item.entity.coach_delivered)}`,
                hasDelivered: true
            };
        }
        if (item.entity?.athlete_received && new Date(item.entity.athlete_received) < now) {
            status.athlete = {
                text: `Received ${formatDateTime(item.entity.athlete_received)}`,
                hasReceived: true
            };
        }
        if (item.entity?.parent_received && new Date(item.entity.parent_received) < now) {
            status.parent = {
                text: `Received ${formatDateTime(item.entity.parent_received)}`,
                hasReceived: true
            };
        }

        return status;
    };

    // Helper function to render content items with delivery status
    const renderContentItems = (content: any[], purpose: string) => {
        if (!content || content.length === 0) return null;
        if (!assignmentHasPurposeEntitlement(assignment, purpose)) return null;

        const isExpanded = expandedPurposes.has(purpose);
        const hasMultipleVersions = content.length > 1;

        // Show only the latest entry (first in array) by default, or all if expanded
        const itemsToShow = isExpanded ? content : [content[0]];

        return (
            <Box>
                {itemsToShow.map((item, index) => {
                    const deliveryStatus = getDeliveryStatus(item);
                    const contentStage = getContentStage(assignment, item, applyRoleLabels);
                    const screenshotUrl = getScreenshotUrl(item);
                    let reportTitle = PurposeNames[purpose as keyof typeof PurposeNames];
                    if (content.length > 1) {
                        reportTitle += ` #${item.id}`;
                    }

                    const segment = 'coach-content';

                    return (
                        <Box key={`${purpose}-${index}`} mb={2} data-testid={`coach-delivery-status`} data-attr={purpose}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <ContentScreenshotThumbnail
                                    screenshotUrl={screenshotUrl}
                                    alt={`${reportTitle} preview`}
                                    data-attr="program-stepper-coach_screenshot-thumbnail"
                                />

                                {/* Content details */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="h6">
                                            <Link data-attr={purpose}
                                                data-testid={purpose === 'lesson_plan' ? 'program-stepper--lesson-plan-link' : undefined}
                                                to={`/${item._type === 'LessonPlan' ? 'lesson-plan' : segment}/${item.id}`}>View {reportTitle}</Link>
                                        </Typography>
                                        <Chip
                                            label={contentStage}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>

                                    {purpose !== 'talking_points' && <>
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
                                            data-testid="coach-delivery-row--athlete"
                                            data-delivered={deliveryStatus.athlete.hasReceived ? 'true' : 'false'}
                                        >
                                            {deliveryStatus.athlete.hasReceived ? (
                                                <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                            ) : (
                                                <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                <strong> {applyRoleLabels('Athlete')}:</strong> {deliveryStatus.athlete.text}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
                                            data-testid="coach-delivery-row--parent"
                                            data-delivered={deliveryStatus.parent.hasReceived ? 'true' : 'false'}
                                        >
                                            {deliveryStatus.parent.hasReceived ? (
                                                <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                            ) : (
                                                <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                <strong> {applyRoleLabels('Guardian')}:</strong> {deliveryStatus.parent.text}
                                            </Typography>
                                        </Box>


                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                            data-testid="coach-delivery-row--coach"
                                            data-delivered={deliveryStatus.coach.hasDelivered ? 'true' : 'false'}
                                        >
                                            {deliveryStatus.coach.hasDelivered ? (
                                                <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                            ) : (
                                                <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                <strong> {applyRoleLabels('Coach')}:</strong> {deliveryStatus.coach.text}
                                            </Typography>
                                        </Box>
                                    </>}
                                </Box>
                            </Box>
                        </Box>
                    );
                })}

                {/* Show Previous Versions button */}
                {hasMultipleVersions && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => toggleExpanded(purpose)}
                            sx={{ textTransform: 'none' }}
                        >
                            {isExpanded ? 'Hide Previous Versions' : `Show Previous Versions (${content.length - 1})`}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            {offeredPlan && (
                <Box sx={{ mb: 2 }}>
                    <SubscriptionPurchaseCta
                        assignmentId={assignmentId}
                        productId={offeredPlan}
                        returnTo={location.pathname}
                        compact
                        membership={assignment.membership}
                    />
                </Box>
            )}

            <Stepper orientation="vertical" data-testid="program-stepper-coach">
                {assignment.assessment_id &&
                    <Step expanded={true} data-testid="program-stepper-coach--pre-assessment">
                        <StepLabel>Pre Assessment</StepLabel>
                        <StepContent>
                            <PreAssessmentCta assignment={assignment} />
                        </StepContent>
                    </Step>
                }
                <Step expanded={docs['feedback_report'] && docs['feedback_report'].length > 0} data-testid="program-stepper-coach--feedback-report">
                    <StepLabel icon={<AssessmentIcon />}>Feedback Report</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['feedback_report'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'feedback_report');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Review detailed feedback and recommendations based on your assessment results.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                <Step expanded={docs['talking_points'] && docs['talking_points'].length > 0} data-testid="program-stepper-coach--talking-points">
                    <StepLabel icon={<SpeakerNotesIcon />}>Talking Points</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['talking_points'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'talking_points');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Key discussion points and conversation starters for meaningful family discussions.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                <Step expanded={docs['curriculum'] && docs['curriculum'].length > 0} data-testid="program-stepper-coach--curriculum">
                    <StepLabel icon={<LibraryBooksIcon />}>12 Session Curriculum</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['curriculum'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'curriculum');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Follow the structured 12-session program designed to enhance mental performance and
                                    resilience.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                <Step expanded={docs['lesson_plan'] && docs['lesson_plan'].length > 0} data-testid="program-stepper-coach--lesson-plan">
                    <StepLabel icon={<MenuBookIcon />}>Lesson Plan</StepLabel>
                    <StepContent>
                        {(() => {
                            const content = docs['lesson_plan'];
                            if (content && content.length > 0) {
                                return renderContentItems(content, 'lesson_plan');
                            }
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    Access detailed lesson plans and resources for each session to maximize your learning
                                    experience.
                                </Typography>
                            );
                        })()}
                    </StepContent>
                </Step>

                {assignment.pre_assessment_submitted_at && (
                    <>
                        <Step expanded={true} data-testid="program-stepper-coach--growth-report-week-4">
                            <StepLabel icon={<TrendingUpIcon />}>Week 4 Growth Report</StepLabel>
                            <StepContent>
                                {(() => {
                                    const content = docs['growth_report_week_4'];
                                    if (content && content.length > 0) {
                                        return renderContentItems(content, 'growth_report_week_4');
                                    }
                                    return (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            data-testid="program-stepper-coach--growth-report-week-4--empty"
                                        >
                                            Not yet generated.
                                        </Typography>
                                    );
                                })()}
                            </StepContent>
                        </Step>

                        <Step expanded={true} data-testid="program-stepper-coach--growth-report-week-8">
                            <StepLabel icon={<TrendingUpIcon />}>Week 8 Growth Report</StepLabel>
                            <StepContent>
                                {(() => {
                                    const content = docs['growth_report_week_8'];
                                    if (content && content.length > 0) {
                                        return renderContentItems(content, 'growth_report_week_8');
                                    }
                                    return (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            data-testid="program-stepper-coach--growth-report-week-8--empty"
                                        >
                                            Not yet generated.
                                        </Typography>
                                    );
                                })()}
                            </StepContent>
                        </Step>
                    </>
                )}

                {assignment.show_post_assessment_step &&
                    <Step expanded={true} data-testid="post-assessment-cta">
                        <StepLabel>Post Assessment</StepLabel>
                        <StepContent>
                            <Typography variant="body2" color="text.secondary">
                                {assignment.post_assessment_submitted_at ? (
                                    <Typography variant="body2" color="text.secondary" data-testid="post-assessment-cta--submitted">
                                        Completed
                                    </Typography>
                                ) : (
                                    (() => {
                                        const postSub = assignment.post_submissions?.[0]
                                            ?? assignment.submissions.find(
                                                sub => sub.entity && !sub.entity.submitted_at &&
                                                    (typeof sub.entity.assessment === 'object'
                                                        ? (sub.entity.assessment as any)?.id === assignment.assessment_id
                                                        : sub.entity.assessment === assignment.assessment_id)
                                            );
                                        const hasAnswered = postSub?.entity?.response_count && postSub.entity.response_count > 0;

                                        return hasAnswered ? (
                                            <Typography variant="body2" color="text.secondary" data-testid="post-assessment-cta--in-progress">
                                                {postSub?.entity?.response_count || 0} questions answered, not yet submitted.
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="post-assessment-cta--not-started">
                                                Not yet completed
                                            </Typography>
                                        );
                                    })()
                                )}
                            </Typography>
                        </StepContent>
                    </Step>
                }

                {assignment.pre_assessment_submitted_at &&
                    <Step expanded={true} data-testid="program-stepper-coach--growth-report">
                        <StepLabel icon={<TrendingUpIcon />}>Growth Report (end of program)</StepLabel>
                        <StepContent>
                            {(() => {
                                const content = docs['growth_report_week_12'];
                                if (content && content.length > 0) {
                                    return renderContentItems(content, 'growth_report_week_12');
                                }
                                return (
                                    <Typography variant="body2" color="text.secondary" data-testid="program-stepper-coach--growth-report--empty">
                                        Not yet generated — queue from the lesson plan at week 12 when that session is complete.
                                    </Typography>
                                );
                            })()}
                        </StepContent>
                    </Step>
                }

            </Stepper>
        </>
    );
};

export default ProgramProgressStepperCoach;
